import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";

type Stats = Awaited<ReturnType<typeof api.adminStats>>;

export default function AdminHome() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.adminStats().then(setStats).catch((e) => setErr(String(e)));
  }, []);

  const customerAccounts = stats?.usersByRole.find((r) => r.role === "customer")?.count ?? 0;
  const siteOwners = stats?.siteOwners ?? 0;
  const employees = stats?.usersByRole.find((r) => r.role === "employee")?.count ?? 0;
  const admins = stats?.usersByRole.find((r) => r.role === "admin")?.count ?? 0;
  const live = stats?.sitesByStatus.find((s) => s.status === "active")?.count ?? 0;
  const drafts = stats?.sitesByStatus.find((s) => s.status === "draft")?.count ?? 0;
  const building = stats?.sitesByStatus.find((s) => s.status === "building")?.count ?? 0;
  const failed = stats?.sitesByStatus.find((s) => s.status === "failed")?.count ?? 0;

  return (
    <div className="admin-page stack">
      <div className="welcome">
        <h1>Admin</h1>
        <p>Staff tools — every action is recorded in the audit log.</p>
      </div>

      {err && <div className="card" style={{ borderColor: "#fee2e2", color: "#991b1b" }}>{err}</div>}

      {/* ── At a glance ─────────────────────────────────────── */}
      <div className="card stack">
        <div style={{ fontSize: 16, fontWeight: 700 }}>At a glance</div>
        <div className="admin-stats">
          <Tile label="Site owners" big={siteOwners} small={`${customerAccounts} customer accounts · ${employees} staff · ${admins} admin`} />
          <Tile label="Live sites" big={live} small={`${drafts} draft · ${building} building${failed ? ` · ${failed} failed` : ""}`} tone="good" />
          <Tile label="Total sites" big={stats?.totalSites ?? 0} small={`${stats?.totalUsers ?? 0} accounts total`} />
        </div>
      </div>

      {/* ── Sections ────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
        <AdminCard
          to="/admin/sites"
          title="All sites"
          blurb="Every customer site, status, owner — staff override and inspect."
          cta="Browse sites →"
        />
        <AdminCard
          to="/start"
          title="Create a website"
          blurb="Find a business or build from scratch with the guided setup."
          cta="New site →"
        />
        <AdminCard
          to="/admin/prospects"
          title="Business finder (PBF)"
          blurb="Explore the map, harvest business details, and create a site from a prospect."
          cta="Open business finder →"
        />
        <AdminCard
          to="/admin/audit"
          title="Audit log"
          blurb="Every staff action — site creates, role changes, build kicks."
          cta="View audit →"
        />
      </div>
    </div>
  );
}

function Tile({ label, big, small, tone }: { label: string; big: number; small: string; tone?: "good" }) {
  const color = tone === "good" ? "#047857" : "var(--ink)";
  return (
    <div className="stat-card">
      <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.04 }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1.1, marginTop: 4 }}>{big}</div>
      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{small}</div>
    </div>
  );
}

function AdminCard({ to, title, blurb, cta }: { to: string; title: string; blurb: string; cta: string }) {
  return (
    <Link to={to} className="admin-card">
      <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
      <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>{blurb}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--primary)" }}>{cta}</div>
    </Link>
  );
}
